/**
 * HTTP client + endpoint wrappers.
 *
 * Uses ``fetch`` directly so the bundle stays small. All endpoints share
 * the same base URL and credential handling.
 */

import type {
  AuditExceptionRow,
  AuditModelRun,
  AuditPromptRow,
  BacktestPnlReport,
  BacktestRunOut,
  BudgetStatus,
  CircuitBreakerResult,
  ExplainResponse,
  ExplainTraceRow,
  FactorDescriptor,
  FundamentalsResponse,
  HealthResponse,
  KellyReport,
  LLMCostSummary,
  MemoCitation,
  MemoSummary,
  OHLCVBar,
  OptimizationResult,
  PolicyVersion,
  PriceTarget,
  Quote,
  RebalanceReport,
  RiskMetrics,
  Score,
  ScoreDecomposition,
  SecurityDetail,
  SecuritySummary,
  SentimentScoreOut,
  SocialPostOut,
  Stance,
  StrategyTemplateOut,
  TopScore,
  WatchdogResult,
} from "./types";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
const API_V1 = `${API_BASE}/api/v1`;

class ApiError extends Error {
  status: number;
  detail?: string;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith("http") ? path : `${API_V1}${path}`;
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...init?.headers,
    },
    ...init,
  });

  if (!res.ok) {
    let detail: string | undefined;
    try {
      const body = await res.json();
      detail = typeof body?.detail === "string" ? body.detail : undefined;
    } catch {
      /* swallow */
    }
    throw new ApiError(res.status, `${init?.method ?? "GET"} ${path} failed: ${res.status}`, detail);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

export { ApiError };

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export async function fetchHealth(): Promise<HealthResponse> {
  return request<HealthResponse>("/health");
}

// ---------------------------------------------------------------------------
// Securities
// ---------------------------------------------------------------------------

export type SecurityListParams = {
  onlyActive?: boolean;
  exchange?: string;
  sector?: string;
  weightVectorId?: number;
  limit?: number;
};

export async function listSecurities(
  params: SecurityListParams = {},
): Promise<SecuritySummary[]> {
  const qs = new URLSearchParams();
  if (params.onlyActive !== undefined) qs.set("only_active", String(params.onlyActive));
  if (params.exchange) qs.set("exchange", params.exchange);
  if (params.sector) qs.set("sector", params.sector);
  if (params.weightVectorId !== undefined)
    qs.set("weight_vector_id", String(params.weightVectorId));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<SecuritySummary[]>(`/securities${suffix}`);
}

export async function getSecurity(id: number): Promise<SecurityDetail> {
  return request<SecurityDetail>(`/securities/${id}`);
}

export async function patchFreshnessOverrides(
  securityId: number,
  overrides: Partial<Record<"prices" | "fundamentals" | "estimates" | "filings", number | null>>,
): Promise<SecurityDetail> {
  return request<SecurityDetail>(`/securities/${securityId}/freshness-overrides`, {
    method: "PATCH",
    body: JSON.stringify(overrides),
  });
}

export async function patchValuationParams(
  securityId: number,
  body: { method: string; target_multiple: number; notes?: string | null },
): Promise<SecurityDetail> {
  return request<SecurityDetail>(`/securities/${securityId}/valuation-params`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function getPriceTarget(securityId: number): Promise<PriceTarget> {
  return request<PriceTarget>(`/securities/${securityId}/price-target`);
}

export async function getQuote(securityId: number): Promise<Quote> {
  return request<Quote>(`/securities/${securityId}/quote`);
}

// ---------------------------------------------------------------------------
// Fundamentals (G5)
// ---------------------------------------------------------------------------

export async function getFundamentals(
  securityId: number,
  opts?: { periodType?: "annual" | "quarterly" | "ttm"; nPeriods?: number },
): Promise<FundamentalsResponse> {
  const qs = new URLSearchParams();
  if (opts?.periodType) qs.set("period_type", opts.periodType);
  if (opts?.nPeriods !== undefined) qs.set("n_periods", String(opts.nPeriods));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<FundamentalsResponse>(`/fundamentals/${securityId}${suffix}`);
}

// ---------------------------------------------------------------------------
// Social / behavioral (G13, G14, G25)
// ---------------------------------------------------------------------------

export type IngestSummary = {
  security_id: number;
  ticker: string;
  as_of: string;
  total_items: number;
  new_posts_inserted: number;
  posts_linked: number;
  sources: string[];
  source_errors: Record<string, string>;
  net_sentiment: number | null;
};

export async function ingestSocial(securityId: number): Promise<IngestSummary> {
  return request<IngestSummary>(`/social/${securityId}/ingest`, { method: "POST" });
}

export async function getSentiment(
  securityId: number,
  platform = "aggregate",
): Promise<SentimentScoreOut> {
  return request<SentimentScoreOut>(
    `/social/${securityId}/sentiment?platform=${encodeURIComponent(platform)}`,
  );
}

export async function listSocialPosts(
  securityId: number,
  opts?: { platform?: string; limit?: number },
): Promise<SocialPostOut[]> {
  const qs = new URLSearchParams();
  if (opts?.platform) qs.set("platform", opts.platform);
  if (opts?.limit !== undefined) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<SocialPostOut[]>(`/social/${securityId}/posts${suffix}`);
}

// ---------------------------------------------------------------------------
// OHLCV price history (G6)
// ---------------------------------------------------------------------------

export async function getPrices(
  securityId: number,
  opts?: {
    fromDate?: string;
    toDate?: string;
    adjusted?: boolean;
    limit?: number;
  },
): Promise<OHLCVBar[]> {
  const qs = new URLSearchParams();
  if (opts?.fromDate) qs.set("from_date", opts.fromDate);
  if (opts?.toDate) qs.set("to_date", opts.toDate);
  if (opts?.adjusted !== undefined) qs.set("adjusted", String(opts.adjusted));
  if (opts?.limit !== undefined) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<OHLCVBar[]>(`/securities/${securityId}/prices${suffix}`);
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

export async function listFactors(): Promise<FactorDescriptor[]> {
  return request<FactorDescriptor[]>("/scoring/factors");
}

export async function listStrategies(): Promise<StrategyTemplateOut[]> {
  return request<StrategyTemplateOut[]>("/scoring/strategies");
}

export async function getStrategy(template: string): Promise<StrategyTemplateOut> {
  return request<StrategyTemplateOut>(`/scoring/strategies/${template}`);
}

export async function topScores(
  weightVectorId: number,
  limit = 20,
): Promise<TopScore[]> {
  return request<TopScore[]>(
    `/scoring/top?weight_vector_id=${weightVectorId}&limit=${limit}`,
  );
}

export async function getScore(securityId: number): Promise<Score> {
  return request<Score>(`/scoring/scores/${securityId}`);
}

export async function getScoreDecomposition(
  securityId: number,
): Promise<ScoreDecomposition[]> {
  return request<ScoreDecomposition[]>(
    `/scoring/scores/${securityId}/decomposition`,
  );
}

export type ScoreHistoryPoint = {
  as_of: string;
  composite: number;
};

export async function getScoreHistory(
  securityId: number,
  opts?: { limit?: number; weightVectorId?: number },
): Promise<ScoreHistoryPoint[]> {
  const qs = new URLSearchParams();
  if (opts?.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts?.weightVectorId !== undefined)
    qs.set("weight_vector_id", String(opts.weightVectorId));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<ScoreHistoryPoint[]>(`/scoring/scores/${securityId}/history${suffix}`);
}

export type FactorHistoryPoint = {
  factor_name: string;
  as_of: string;
  sector_z_score: number | null;
};

export async function getFactorHistory(
  securityId: number,
  opts?: { days?: number; factorName?: string },
): Promise<FactorHistoryPoint[]> {
  const qs = new URLSearchParams();
  if (opts?.days !== undefined) qs.set("days", String(opts.days));
  if (opts?.factorName !== undefined) qs.set("factor_name", opts.factorName);
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<FactorHistoryPoint[]>(
    `/scoring/scores/${securityId}/factor-history${suffix}`,
  );
}

export async function runScoring(payload: {
  weight_vector_id: number;
  as_of: string;
  universe?: number[] | null;
}): Promise<{
  model_run_id: number;
  weight_vector_id: number;
  as_of: string;
  universe_size: number;
  scored: number;
  factor_rows: number;
  decomposition_rows: number;
}> {
  return request("/scoring/run", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

export async function listPolicyVersions(): Promise<PolicyVersion[]> {
  return request<PolicyVersion[]>("/policy/versions");
}

export async function getActivePolicy(): Promise<PolicyVersion | null> {
  return request<PolicyVersion | null>("/policy/versions/active");
}

export async function getPolicyVersion(id: number): Promise<PolicyVersion> {
  return request<PolicyVersion>(`/policy/versions/${id}`);
}

export async function activatePolicyVersion(id: number): Promise<void> {
  await request<void>(`/policy/versions/${id}/activate`, { method: "POST" });
}

export async function seedDefaultPolicy(): Promise<PolicyVersion> {
  return request<PolicyVersion>("/policy/seed-default", { method: "POST" });
}

export type RuleInPayload = {
  priority: number;
  name: string;
  conditions: Record<string, unknown>;
  output_stance: string;
  explanation_template?: string | null;
};

export async function createPolicyVersion(payload: {
  name: string;
  description?: string | null;
  rules: RuleInPayload[];
  activate?: boolean;
}): Promise<PolicyVersion> {
  return request<PolicyVersion>("/policy/versions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type ExplainPayload = {
  policy_version_id?: number | null;
  security_id?: number | null;
  inputs?: {
    composite: number;
    confidence_band: string;
    thesis_status: string;
    valuation_score?: number | null;
    quality_score?: number | null;
    growth_score?: number | null;
    momentum_score?: number | null;
    revisions_score?: number | null;
    risk_score?: number | null;
    coverage?: number | null;
  } | null;
};

export async function explainPolicy(
  payload: ExplainPayload,
): Promise<ExplainResponse> {
  return request<ExplainResponse>("/policy/explain", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function latestStance(securityId: number): Promise<Stance> {
  return request<Stance>(`/policy/stances/${securityId}`);
}

// Reuse imported type to satisfy TS' `noUnusedLocals` when bundle-tree-shaking.
export type { ExplainTraceRow };

// ---------------------------------------------------------------------------
// Memos
// ---------------------------------------------------------------------------

export async function listMemos(limit = 50): Promise<MemoSummary[]> {
  return request<MemoSummary[]>(`/memos?limit=${limit}`);
}

export async function getMemo(securityId: number): Promise<MemoSummary> {
  return request<MemoSummary>(`/memos/${securityId}`);
}

export async function getMemoMarkdown(securityId: number): Promise<string> {
  return request<string>(`/memos/${securityId}/markdown`);
}

export async function getMemoHtml(securityId: number): Promise<string> {
  const url = `${API_V1}/memos/${securityId}/html`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new ApiError(res.status, `GET memo html failed: ${res.status}`);
  }
  return await res.text();
}

export async function getMemoCitations(securityId: number): Promise<MemoCitation[]> {
  return request<MemoCitation[]>(`/memos/${securityId}/citations`);
}

export async function generateMemo(payload: {
  security_id: number;
  as_of?: string | null;
  weight_vector_id?: number | null;
  policy_version_id?: number | null;
}): Promise<{
  model_run_id: number;
  memo_id: number;
  security_id: number;
  as_of: string;
  ai_assisted: boolean;
  llm_model: string | null;
  fallback_sections: string[];
  all_validations_passed: boolean;
}> {
  return request("/memos/generate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export async function listAuditRuns(
  params: { kind?: string; status?: string; limit?: number } = {},
): Promise<AuditModelRun[]> {
  const qs = new URLSearchParams();
  if (params.kind) qs.set("kind", params.kind);
  if (params.status) qs.set("status", params.status);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<AuditModelRun[]>(`/audit/runs${suffix}`);
}

export async function listAuditPrompts(
  params: { template_id?: string; model?: string; limit?: number } = {},
): Promise<AuditPromptRow[]> {
  const qs = new URLSearchParams();
  if (params.template_id) qs.set("template_id", params.template_id);
  if (params.model) qs.set("model", params.model);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<AuditPromptRow[]>(`/audit/prompts${suffix}`);
}

export async function listAuditExceptions(
  params: { severity?: string; service?: string; limit?: number } = {},
): Promise<AuditExceptionRow[]> {
  const qs = new URLSearchParams();
  if (params.severity) qs.set("severity", params.severity);
  if (params.service) qs.set("service", params.service);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<AuditExceptionRow[]>(`/audit/exceptions${suffix}`);
}

// ---------------------------------------------------------------------------
// Governance
// ---------------------------------------------------------------------------

export type DriftSection = {
  name: string;
  reference_as_of: string;
  current_as_of: string;
  psi: number;
  severity: "stable" | "moderate" | "material";
  n_buckets: number;
  n_reference: number;
  n_current: number;
  bucket_edges: number[];
  bucket_psi: number[];
};

export type DriftReport = {
  weight_vector_id: number;
  material_count: number;
  moderate_count: number;
  sections: DriftSection[];
};

export async function getDriftReport(payload: {
  weight_vector_id: number;
  reference_as_of: string;
  current_as_of: string;
  factor_names?: string[];
}): Promise<DriftReport> {
  return request<DriftReport>("/governance/drift", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type RankingChurn = {
  weight_vector_id: number;
  from: string;
  to: string;
  spearman: number | null;
  top_n_retention: Record<string, number>;
  mean_rank_churn: number | null;
  n_overlap: number;
};

export async function getRankingChurn(payload: {
  weight_vector_id: number;
  from_date: string;
  to_date: string;
}): Promise<RankingChurn> {
  return request<RankingChurn>("/governance/ranking-churn", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type AlertOut = {
  id: number;
  alert_type: string;
  severity: "info" | "warning" | "critical";
  title: string;
  payload: Record<string, unknown> | null;
  triggered_at: string;
  acknowledged_at: string | null;
  acknowledged_by: number | null;
  security_id: number | null;
  portfolio_id: number | null;
};

export async function listAlerts(
  params: {
    severity?: "info" | "warning" | "critical";
    alert_type?: string;
    only_open?: boolean;
    limit?: number;
  } = {},
): Promise<AlertOut[]> {
  const qs = new URLSearchParams();
  if (params.severity) qs.set("severity", params.severity);
  if (params.alert_type) qs.set("alert_type", params.alert_type);
  if (params.only_open !== undefined) qs.set("only_open", String(params.only_open));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<AlertOut[]>(`/governance/alerts${suffix}`);
}

export async function ackAlert(id: number): Promise<void> {
  await request<void>(`/governance/alerts/${id}/ack`, { method: "POST" });
}

export async function detectFreshness(payload: {
  as_of?: string;
  thresholds?: Record<string, number>;
  emit_alerts?: boolean;
}): Promise<{
  findings: Array<{
    domain: string;
    security_id: number;
    ticker: string | null;
    last_seen: string | null;
    age_days: number | null;
    threshold_days: number;
  }>;
  alerts_emitted: number;
}> {
  return request("/governance/freshness", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function detectAnomalies(payload: {
  weight_vector_id: number;
  as_of: string;
  sigma_threshold?: number;
  min_abs_threshold?: number;
  emit_alerts?: boolean;
}): Promise<{
  anomalies: Array<{
    security_id: number;
    ticker: string | null;
    as_of: string;
    prior_as_of: string;
    composite: number;
    prior_composite: number;
    delta: number;
    z_delta: number | null;
  }>;
  alerts_emitted: number;
}> {
  return request("/governance/anomalies", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function compareChallenger(payload: {
  champion_weight_vector_id: number;
  challenger_weight_vector_id: number;
  as_of: string;
  movers_k?: number;
}): Promise<{
  champion_weight_vector_id: number;
  challenger_weight_vector_id: number;
  n_overlap: number;
  spearman: number | null;
  top_n_agreement: Record<string, number>;
  mean_abs_delta: number | null;
  median_delta: number | null;
  biggest_movers: Array<{
    security_id: number;
    champion_score: number;
    challenger_score: number;
    delta: number;
  }>;
}> {
  return request("/governance/challenger", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Backtest validation
// ---------------------------------------------------------------------------

export async function triggerBacktest(payload: {
  weight_vector_id: number;
  lookback_days?: number;
}): Promise<BacktestRunOut> {
  return request<BacktestRunOut>("/governance/backtest", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listBacktestRuns(
  params: { weight_vector_id?: number; limit?: number } = {},
): Promise<BacktestRunOut[]> {
  const qs = new URLSearchParams();
  if (params.weight_vector_id !== undefined)
    qs.set("weight_vector_id", String(params.weight_vector_id));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<BacktestRunOut[]>(`/governance/backtest/runs${suffix}`);
}

export async function getBacktestRun(runId: number): Promise<BacktestRunOut> {
  return request<BacktestRunOut>(`/governance/backtest/runs/${runId}`);
}

// ---------------------------------------------------------------------------
// Portfolios
// ---------------------------------------------------------------------------

export type PortfolioOut = {
  id: number;
  name: string;
  description: string | null;
  base_currency: string;
  inception_date: string | null;
};

export type HoldingOut = {
  id: number;
  portfolio_id: number;
  security_id: number;
  ticker: string | null;
  as_of: string;
  shares: number;
  cost_basis: number | null;
  avg_cost: number | null;
  notes: string | null;
};

export type PortfolioSummaryOut = {
  portfolio_id: number;
  name: string;
  base_currency: string;
  n_holdings: number;
  market_value: number | null;
  avg_composite: number | null;
  insufficient_count: number;
  deteriorating_count: number;
  concentration_index: number | null;
  sector_exposure: Array<{ bucket: string; weight: number; n_securities: number }>;
  exchange_exposure: Array<{ bucket: string; weight: number; n_securities: number }>;
  holdings: Array<{
    security_id: number;
    ticker: string | null;
    shares: number;
    market_value: number | null;
    weight: number | null;
    composite: number | null;
    confidence_band: string | null;
    thesis_status: string | null;
    as_of: string | null;
    cost_basis: number | null;
    avg_cost: number | null;
    notes: string | null;
  }>;
};

export async function listPortfoliosApi(): Promise<PortfolioOut[]> {
  return request<PortfolioOut[]>("/portfolios");
}

export async function createPortfolio(payload: {
  name: string;
  description?: string | null;
  base_currency?: string;
  inception_date?: string | null;
}): Promise<PortfolioOut> {
  return request<PortfolioOut>("/portfolios", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listHoldings(
  portfolioId: number,
  asOf?: string,
): Promise<HoldingOut[]> {
  const qs = asOf ? `?as_of=${asOf}` : "";
  return request<HoldingOut[]>(`/portfolios/${portfolioId}/holdings${qs}`);
}

export async function upsertHolding(
  portfolioId: number,
  payload: {
    security_id: number;
    as_of: string;
    shares: number;
    cost_basis?: number | null;
    avg_cost?: number | null;
    notes?: string | null;
  },
): Promise<HoldingOut> {
  return request<HoldingOut>(`/portfolios/${portfolioId}/holdings`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteHolding(
  portfolioId: number,
  securityId: number,
  asOf: string,
): Promise<void> {
  await request<void>(
    `/portfolios/${portfolioId}/holdings/${securityId}/${asOf}`,
    { method: "DELETE" },
  );
}

export async function deletePortfolio(portfolioId: number): Promise<void> {
  await request<void>(`/portfolios/${portfolioId}`, { method: "DELETE" });
}

export async function getPortfolioSummary(
  portfolioId: number,
  weightVectorId?: number,
): Promise<PortfolioSummaryOut> {
  const qs = weightVectorId !== undefined ? `?weight_vector_id=${weightVectorId}` : "";
  return request<PortfolioSummaryOut>(
    `/portfolios/${portfolioId}/summary${qs}`,
  );
}

// ---------------------------------------------------------------------------
// M1 — Risk-profiled portfolio recommender
// ---------------------------------------------------------------------------

export type RecommendedPosition = {
  security_id: number;
  ticker: string;
  composite: number;
  confidence_band: string;
  weight: number;
  allocation_usd: number;
  quarter_kelly: number | null;
};

export type PortfolioRecommendation = {
  risk_appetite: string;
  capital: number;
  weight_vector_id: number;
  optimizer_mode: string;
  gross_exposure: number;
  deployed_usd: number;
  cash_usd: number;
  expected_vol: number | null;
  expected_return: number | null;
  sharpe: number | null;
  realised_vol: number | null;
  as_of: string | null;
  positions: RecommendedPosition[];
  rationale: string[];
};

export async function recommendPortfolio(payload: {
  risk_appetite: "conservative" | "balanced" | "aggressive";
  capital: number;
  weight_vector_id?: number | null;
  top_n?: number | null;
}): Promise<PortfolioRecommendation> {
  return request<PortfolioRecommendation>("/portfolios/recommend", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// M2 — Position action engine
// ---------------------------------------------------------------------------

export type PositionAction = {
  security_id: number;
  ticker: string;
  action: "ACCUMULATE" | "HOLD" | "REDUCE" | "EXIT" | "REVIEW";
  stance_label: string;
  composite: number;
  confidence_band: string;
  thesis_status: string;
  current_weight: number | null;
  target_price: number | null;
  current_price: number | null;
  upside_pct: number | null;
  reason: string;
};

export type PositionActions = {
  portfolio_id: number;
  weight_vector_id: number;
  as_of: string | null;
  summary: Record<string, number>;
  positions: PositionAction[];
};

export async function getPositionActions(
  portfolioId: number,
  weightVectorId = 1,
): Promise<PositionActions> {
  return request<PositionActions>(
    `/portfolios/${portfolioId}/actions?weight_vector_id=${weightVectorId}`,
  );
}

// ---------------------------------------------------------------------------
// M4 — position-action alert scan
// ---------------------------------------------------------------------------

export type ActionAlertOutcome = {
  security_id: number;
  ticker: string;
  action: string;
  prior_action: string | null;
  fired: boolean;
  reason_skipped: string | null;
};

export type ActionAlertScan = {
  portfolio_id: number;
  weight_vector_id: number;
  holdings_checked: number;
  alerts_fired: number;
  deliver_webhook: boolean;
  outcomes: ActionAlertOutcome[];
};

export async function scanActionAlerts(
  portfolioId: number,
  opts?: { weightVectorId?: number; deliverWebhook?: boolean },
): Promise<ActionAlertScan> {
  const qs = new URLSearchParams();
  if (opts?.weightVectorId !== undefined) qs.set("weight_vector_id", String(opts.weightVectorId));
  if (opts?.deliverWebhook !== undefined) qs.set("deliver_webhook", String(opts.deliverWebhook));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<ActionAlertScan>(
    `/portfolios/${portfolioId}/action-alerts/scan${suffix}`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------------------
// Scoring coverage (D2/D5 health metric)
// ---------------------------------------------------------------------------

export type CoverageRow = {
  weight_vector_id: number;
  name: string;
  applicable_security_type: string | null;
  universe_size: number;
  scored: number;
  coverage_pct: number;
  latest_as_of: string | null;
};

export async function getScoringCoverage(): Promise<CoverageRow[]> {
  return request<CoverageRow[]>("/scoring/coverage");
}

// ---------------------------------------------------------------------------
// Watchlists
// ---------------------------------------------------------------------------

export type WatchlistOut = {
  id: number;
  name: string;
  description: string | null;
};

export type WatchlistMemberOut = {
  id: number;
  watchlist_id: number;
  security_id: number;
  ticker: string | null;
  company_name: string | null;
  notes: string | null;
  composite: number | null;
  confidence_band: string | null;
  thesis_status: string | null;
};

export type WatchlistDetailOut = WatchlistOut & {
  members: WatchlistMemberOut[];
};

export async function listWatchlists(): Promise<WatchlistOut[]> {
  return request<WatchlistOut[]>("/watchlists");
}

export async function createWatchlist(payload: {
  name: string;
  description?: string | null;
}): Promise<WatchlistDetailOut> {
  return request<WatchlistDetailOut>("/watchlists", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getWatchlist(id: number): Promise<WatchlistDetailOut> {
  return request<WatchlistDetailOut>(`/watchlists/${id}`);
}

export async function deleteWatchlist(id: number): Promise<void> {
  await request<void>(`/watchlists/${id}`, { method: "DELETE" });
}

export async function addWatchlistMember(
  watchlistId: number,
  payload: { security_id: number; notes?: string | null },
): Promise<WatchlistMemberOut> {
  return request<WatchlistMemberOut>(`/watchlists/${watchlistId}/members`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function removeWatchlistMember(
  watchlistId: number,
  securityId: number,
): Promise<void> {
  await request<void>(`/watchlists/${watchlistId}/members/${securityId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Weight vectors (Settings page)
// ---------------------------------------------------------------------------

export type WeightVectorOut = {
  id: number;
  name: string;
  description: string | null;
  version: number;
  weights: Record<string, number>;
  strategy_template: string;
  applicable_security_type: string | null;
  is_active: boolean;
  created_by: number | null;
};

export async function listWeightVectors(opts?: {
  onlyLatest?: boolean;
}): Promise<WeightVectorOut[]> {
  const qs = opts?.onlyLatest ? "?only_latest=true" : "";
  return request<WeightVectorOut[]>(`/weight-vectors${qs}`);
}

export async function getWeightVector(id: number): Promise<WeightVectorOut> {
  return request<WeightVectorOut>(`/weight-vectors/${id}`);
}

export async function createWeightVector(payload: {
  name: string;
  description?: string | null;
  weights: Record<string, number>;
  strategy_template?: string;
  applicable_security_type?: string | null;
  activate?: boolean;
}): Promise<WeightVectorOut> {
  return request<WeightVectorOut>("/weight-vectors", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function activateWeightVector(id: number): Promise<WeightVectorOut> {
  return request<WeightVectorOut>(`/weight-vectors/${id}/activate`, {
    method: "POST",
  });
}

// G17 — seed every non-CUSTOM strategy template into a WeightVector.
// Idempotent on the server; safe to call from the Strategies page on load.
export async function seedStrategies(): Promise<Record<string, string>> {
  return request<Record<string, string>>("/scoring/seed-strategies", {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Portfolio risk dashboard (G11)
// ---------------------------------------------------------------------------

export async function getPortfolioRisk(
  portfolioId: number,
  opts?: { lookbackDays?: number },
): Promise<RiskMetrics> {
  const qs = opts?.lookbackDays ? `?lookback_days=${opts.lookbackDays}` : "";
  return request<RiskMetrics>(`/portfolios/${portfolioId}/risk${qs}`);
}

// ---------------------------------------------------------------------------
// Portfolio optimizer (G9)
// ---------------------------------------------------------------------------

export async function optimizePortfolio(
  portfolioId: number,
  payload: {
    mode: "equal_weight" | "min_variance" | "risk_parity" | "max_sharpe";
    lookback_days?: number;
    max_weight?: number;
  },
): Promise<OptimizationResult> {
  return request<OptimizationResult>(`/portfolios/${portfolioId}/optimize`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Kelly sizer (G10)
// ---------------------------------------------------------------------------

export async function getPortfolioKelly(
  portfolioId: number,
  opts?: { lookbackDays?: number; volTarget?: number; maxPosition?: number },
): Promise<KellyReport> {
  const qs = new URLSearchParams();
  if (opts?.lookbackDays) qs.set("lookback_days", String(opts.lookbackDays));
  if (opts?.volTarget) qs.set("vol_target", String(opts.volTarget));
  if (opts?.maxPosition) qs.set("max_position", String(opts.maxPosition));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<KellyReport>(`/portfolios/${portfolioId}/kelly${suffix}`);
}

// ---------------------------------------------------------------------------
// Rebalance engine (G12)
// ---------------------------------------------------------------------------

export async function getRebalanceProposal(
  portfolioId: number,
  opts?: { portfolioValue?: number; minTradeBps?: number; tcBps?: number },
): Promise<RebalanceReport> {
  const qs = new URLSearchParams();
  if (opts?.portfolioValue) qs.set("portfolio_value", String(opts.portfolioValue));
  if (opts?.minTradeBps) qs.set("min_trade_bps", String(opts.minTradeBps));
  if (opts?.tcBps) qs.set("tc_bps", String(opts.tcBps));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<RebalanceReport>(`/portfolios/${portfolioId}/rebalance${suffix}`);
}

// ---------------------------------------------------------------------------
// Circuit-breaker (G21)
// ---------------------------------------------------------------------------

export async function getCircuitBreakerStatus(
  portfolioId: number,
): Promise<CircuitBreakerResult> {
  return request<CircuitBreakerResult>(`/portfolios/${portfolioId}/circuit-breaker`);
}

export async function setCircuitBreakerConfig(
  portfolioId: number,
  config: { soft_threshold: number; hard_threshold: number } | null,
): Promise<PortfolioOut> {
  return request<PortfolioOut>(`/portfolios/${portfolioId}/circuit-breaker-config`, {
    method: "PUT",
    body: JSON.stringify({ circuit_breaker_config: config }),
  });
}

// ---------------------------------------------------------------------------
// Watchdogs (G15)
// ---------------------------------------------------------------------------

export async function runWatchdogs(opts?: {
  weightVectorIds?: number[];
  heartbeatMaxAgeDays?: number;
}): Promise<WatchdogResult[]> {
  const payload: Record<string, unknown> = {};
  if (opts?.weightVectorIds) payload.weight_vector_ids = opts.weightVectorIds;
  if (opts?.heartbeatMaxAgeDays) payload.heartbeat_max_age_days = opts.heartbeatMaxAgeDays;
  return request<WatchdogResult[]>("/governance/watchdogs", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// LLM cost tracker (G22)
// ---------------------------------------------------------------------------

export async function getLLMCosts(opts?: {
  year?: number;
  month?: number;
}): Promise<LLMCostSummary> {
  const qs = new URLSearchParams();
  if (opts?.year) qs.set("year", String(opts.year));
  if (opts?.month) qs.set("month", String(opts.month));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<LLMCostSummary>(`/governance/llm-costs${suffix}`);
}

export async function getLLMBudget(): Promise<BudgetStatus> {
  return request<BudgetStatus>("/governance/llm-budget");
}

// ---------------------------------------------------------------------------
// Backtest P&L (G8)
// ---------------------------------------------------------------------------

export async function runBacktestPnl(payload: {
  weight_vector_id: number;
  as_of_dates: string[];
  universe?: number[] | null;
  top_n?: number;
  tc_bps?: number;
}): Promise<BacktestPnlReport> {
  return request<BacktestPnlReport>("/backtest/pnl", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Distinct scored as-of dates available to backtest a weight vector. */
export async function getBacktestAvailableDates(
  weightVectorId: number,
  lookbackDays = 365,
): Promise<string[]> {
  const qs = new URLSearchParams({
    weight_vector_id: String(weightVectorId),
    lookback_days: String(lookbackDays),
  });
  return request<string[]>(`/backtest/available-dates?${qs}`);
}

// ---------------------------------------------------------------------------
// Daily briefing (G26)
// ---------------------------------------------------------------------------

import type { DailyBriefing } from "./types";

export async function getDailyBriefing(opts?: {
  as_of?: string;
  weight_vector_id?: number;
  movers_top_n?: number;
  include_narrative?: boolean;
}): Promise<DailyBriefing> {
  const qs = new URLSearchParams();
  if (opts?.as_of) qs.set("as_of", opts.as_of);
  if (opts?.weight_vector_id !== undefined)
    qs.set("weight_vector_id", String(opts.weight_vector_id));
  if (opts?.movers_top_n !== undefined)
    qs.set("movers_top_n", String(opts.movers_top_n));
  if (opts?.include_narrative !== undefined)
    qs.set("include_narrative", String(opts.include_narrative));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<DailyBriefing>(`/briefing/daily${suffix}`);
}

export async function getDailyBriefingMarkdown(opts?: {
  as_of?: string;
  weight_vector_id?: number;
  movers_top_n?: number;
  include_narrative?: boolean;
}): Promise<string> {
  const qs = new URLSearchParams();
  if (opts?.as_of) qs.set("as_of", opts.as_of);
  if (opts?.weight_vector_id !== undefined)
    qs.set("weight_vector_id", String(opts.weight_vector_id));
  if (opts?.movers_top_n !== undefined)
    qs.set("movers_top_n", String(opts.movers_top_n));
  if (opts?.include_narrative !== undefined)
    qs.set("include_narrative", String(opts.include_narrative));
  const suffix = qs.toString() ? `?${qs}` : "";
  const url = `${API_V1}/briefing/daily.md${suffix}`;
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "text/plain" },
  });
  if (!res.ok) {
    throw new ApiError(res.status, `GET /briefing/daily.md failed: ${res.status}`);
  }
  return res.text();
}
